'use client';

import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** Clear only app-specific cached state — NOT Supabase auth tokens */
function clearAppCachedState() {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Only remove app-specific keys, NOT Supabase auth tokens (sb_ prefix)
      if (key && !key.startsWith('sb_') && !key.startsWith('supabase')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {}
  try {
    sessionStorage.clear();
  } catch {}
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Stable client ref — never recreated across renders
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      console.log('[Auth Debug] Initializing auth...');

      // Step 1: Try getSession() — reads from local cache (cookies / localStorage)
      console.log('[Auth Debug] Checking getSession...');
      try {
        const { data: { session: cachedSession } } = await supabase.auth.getSession();
        console.log('[Auth Debug] Session result:', cachedSession ? `exists (user: ${cachedSession.user?.id})` : 'null');

        if (cachedSession?.user) {
          if (mounted) {
            setSession(cachedSession);
            setUser(cachedSession.user);
            setLoading(false);
          }
          console.log('[Auth Debug] Final resolved userId:', cachedSession.user.id);
          return;
        }
      } catch (e) {
        console.warn('[Auth Debug] getSession() threw:', e);
      }

      // Step 2: getUser() — network call, authoritative source
      console.log('[Auth Debug] Checking getUser...');
      try {
        const { data: { user: networkUser }, error } = await supabase.auth.getUser();
        console.log('[Auth Debug] User result:', networkUser ? `id=${networkUser.id}` : 'null', error ? `error=${error.message}` : '');

        if (networkUser) {
          // Attempt to refresh session to get tokens
          try {
            const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
            if (mounted) {
              setSession(refreshedSession ?? null);
              setUser(refreshedSession?.user ?? networkUser);
              setLoading(false);
            }
            console.log('[Auth Debug] Final resolved userId:', (refreshedSession?.user ?? networkUser).id);
          } catch {
            if (mounted) {
              setSession(null);
              setUser(networkUser);
              setLoading(false);
            }
            console.log('[Auth Debug] Final resolved userId:', networkUser.id);
          }
          return;
        }
      } catch (e) {
        console.warn('[Auth Debug] getUser() threw:', e);
      }

      // Step 3: No authenticated user found
      console.log('[Auth Debug] Final resolved userId: null — not authenticated');
      if (mounted) {
        setSession(null);
        setUser(null);
        setLoading(false);
      }
    }

    initAuth();

    // Listen for auth state changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log('[Auth Debug] onAuthStateChange event:', event, 'user:', newSession?.user?.id ?? 'null');

      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        // Only clear app-specific state, NOT Supabase auth tokens
        clearAppCachedState();
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        setSession(newSession);
        setUser(newSession?.user ?? null);
      } else {
        setSession(newSession);
        setUser(newSession?.user ?? null);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: { fullName?: string; avatarUrl?: string } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    // Immediately sync state after sign in
    if (data.session) {
      setSession(data.session);
      setUser(data.session.user);
    }
    return data;
  };

  // Sign Out — clears app state via onAuthStateChange SIGNED_OUT handler
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User (network call)
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
